import { useEffect, useState, useMemo, useCallback } from "react";
import { groupProjectsInCart } from "../../api/utils";
import Footer from "common/src/components/Footer";
import Navbar from "../../common/Navbar";
import Breadcrumb from "../../common/Breadcrumb";
import { EmptyCart } from "./EmptyCart";
import { Header } from "./Header";
import { useCartStorage } from "../../../store";
import { CartWithProjects } from "./CartWithProjects";
import { SummaryContainer } from "./SummaryContainer";
import { useDataLayer } from "data-layer";
import { createCartProjectFromApplication } from "../../discovery/ExploreApplicationsPage";
import { getBalance } from "@wagmi/core";
import { useAccount } from "wagmi";
import { config } from "../../../app/wagmi";
import { zeroAddress } from "viem";
import { NATIVE, getChainById, getMultiplier } from "common";
import { formatUnits } from "ethers/lib/utils";
import GenericModal from "../../common/GenericModal";
import SquidWidget from "./SquidWidget";
import { jsx as _jsx, Fragment as _Fragment, jsxs as _jsxs } from "react/jsx-runtime";
export default function ViewCart() {
  const {
    projects,
    setCart,
    getVotingTokenForChain
  } = useCartStorage();
  const {
    address,
    chainId: connectedChain
  } = useAccount();
  const [{
    balances,
    addressOfBalances
  }, setBalances] = useState({
    balances: {}
  });
  const [enoughBalanceByChainId, setEnoughBalanceByChainId] = useState({});
  const dataLayer = useDataLayer();
  const [chainIds, groupedCartProjects] = useMemo(() => {
    const groupedCartProjects = groupProjectsInCart(projects);
    const chainIds = Object.keys(groupedCartProjects);
    return [chainIds, groupedCartProjects];
  }, [projects]);
  const [openSwapModel, setOpenSwapModal] = useState(false);
  const [swapParams, setSwapParams] = useState({
    fromChainId: "8453",
    toChainId: "42161",
    fromTokenAddress: NATIVE,
    toTokenAddress: NATIVE
  });
  const handleSwap = params => {
    setSwapParams(params);
    setOpenSwapModal(true);
  };

  // ensure cart data is up to date on mount
  useEffect(() => {
    const applicationRefs = projects.map(project => {
      return {
        chainId: project.chainId,
        roundId: project.roundId,
        id: project.applicationIndex.toString()
      };
    });

    // only update cart if fetching applications is successful
    dataLayer.getApprovedApplicationsByExpandedRefs(applicationRefs).then(applications => {
      const updatedProjects = applications.flatMap(application => {
        var _existingProject$amou;
        const existingProject = projects.find(project => {
          return applications.some(application => application.chainId === project.chainId && application.roundId === project.roundId && application.roundApplicationId === project.applicationIndex.toString());
        });
        const newProject = createCartProjectFromApplication(application);

        // update all application data, but preserve the selected amount
        return {
          ...newProject,
          amount: (_existingProject$amou = existingProject === null || existingProject === void 0 ? void 0 : existingProject.amount) !== null && _existingProject$amou !== void 0 ? _existingProject$amou : ""
        };
      });

      // replace whole cart
      setCart(updatedProjects);
    }).catch(error => {
      console.error("error fetching applications in cart", error);
    });

    // we only want to run this once on mount
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  const totalAmountByChainId = useMemo(() => Object.keys(groupedCartProjects).reduce((acc, chainId) => {
    const amount = Object.values(groupedCartProjects[Number(chainId)]).reduce((acc, curr) => acc + curr.reduce((acc, curr) => acc + (Number(curr.amount) || 0), 0), 0);
    return {
      ...acc,
      [Number(chainId)]: amount
    };
  }, {}), [groupedCartProjects]);
  const fetchBalances = useCallback(async () => {
    const allBalances = await Promise.all(chainIds.map(async chainId => {
      const chainIdNumber = Number(chainId);
      const chain = getChainById(chainIdNumber);
      const chainBalances = await Promise.all(chain.tokens.map(async token => {
        if (!token.canVote || !address) return null;
        try {
          const balance = await getBalance(config, {
            address,
            token: token.address === zeroAddress || token.address.toLowerCase() === NATIVE.toLowerCase() ? undefined : token.address.toLowerCase(),
            chainId: chainIdNumber
          });
          return {
            ...balance,
            address: token.address,
            chainId: chainIdNumber,
            formattedAmount: Number(formatUnits(balance.value / BigInt(getMultiplier(chainIdNumber)), balance.decimals))
          };
        } catch (e) {
          console.error(`Error fetching balance for chain ${chainIdNumber} and token ${token.address}`, e);
          return null;
        }
      }));

      // Filter and convert to balanceMap directly
      const balanceMap = chainBalances.reduce((map, balance) => {
        if (balance) {
          map[balance.address.toLowerCase()] = balance; // Use balance directly as it's already typed
        }
        return map;
      }, {});
      return {
        chainId: chainIdNumber,
        balanceMap
      };
    }));

    // Convert array of balances into a single BalanceMap object
    const newBalances = allBalances.reduce((map, _ref) => {
      let {
        chainId,
        balanceMap
      } = _ref;
      map[chainId] = balanceMap;
      return map;
    }, {});
    setBalances({
      balances: newBalances,
      addressOfBalances: address
    });
  }, [address, chainIds]);
  useEffect(() => {
    // Fetch balances if they have not been fetched yet
    if (address !== addressOfBalances) {
      fetchBalances();
    }
  }, [address, addressOfBalances, fetchBalances]);
  useEffect(() => {
    if (Object.keys(balances).length > 0 && Object.keys(totalAmountByChainId).length > 0) {
      const enoughBalanceByChainId = Object.keys(totalAmountByChainId).reduce((acc, chainId) => {
        var _balances$Number$getV;
        const totalAmount = totalAmountByChainId[Number(chainId)];
        const balance = ((_balances$Number$getV = balances[Number(chainId)][getVotingTokenForChain(Number(chainId)).address.toLowerCase()]) === null || _balances$Number$getV === void 0 ? void 0 : _balances$Number$getV.formattedAmount) || 0;
        acc[Number(chainId)] = balance >= totalAmount;
        return acc;
      }, {});
      setEnoughBalanceByChainId(enoughBalanceByChainId);
    }
  }, [balances, totalAmountByChainId, getVotingTokenForChain]);
  const breadCrumbs = [{
    name: "Explorer Home",
    path: "/"
  }, {
    name: "Cart",
    path: `/cart`
  }];
  const swap = _chainId => {
    const chainId = Number(_chainId);
    const isConnectedChain = !connectedChain || connectedChain === chainId;
    handleSwap({
      fromChainId: isConnectedChain ? "1" : connectedChain.toString(),
      toChainId: chainId.toString(),
      fromTokenAddress: NATIVE,
      toTokenAddress: getVotingTokenForChain(chainId).address
    });
  };
  const swapModalHandler = async flag => {
    await fetchBalances();
    setOpenSwapModal(flag);
  };
  return /*#__PURE__*/_jsxs(_Fragment, {
    children: [/*#__PURE__*/_jsx(Navbar, {}), /*#__PURE__*/_jsxs("div", {
      className: "relative top-28 lg:mx-20 min-h-screen h-auto sm:px-4 px-2 py-7 lg:pt-0 font-sans",
      children: [/*#__PURE__*/_jsx("div", {
        className: "flex flex-col pb-4",
        "data-testid": "bread-crumbs",
        children: /*#__PURE__*/_jsx(Breadcrumb, {
          items: breadCrumbs
        })
      }), /*#__PURE__*/_jsxs("main", {
        children: [/*#__PURE__*/_jsx(Header, {
          projects: projects
        }), /*#__PURE__*/_jsx("div", {
          className: "flex flex-col md:flex-row gap-5",
          children: projects.length === 0 ? /*#__PURE__*/_jsxs(_Fragment, {
            children: [/*#__PURE__*/_jsx(EmptyCart, {}), /*#__PURE__*/_jsx(SummaryContainer, {
              enoughBalanceByChainId: enoughBalanceByChainId,
              totalAmountByChainId: totalAmountByChainId,
              handleSwap: swap
            })]
          }) : /*#__PURE__*/_jsxs("div", {
            className: "grid grid-cols-1 sm:grid-cols-3 gap-5 w-full",
            children: [/*#__PURE__*/_jsx("div", {
              className: "flex flex-col gap-5 sm:col-span-2 order-1",
              children: Object.keys(groupedCartProjects).map(chainId => /*#__PURE__*/_jsx("div", {
                className: "w-full",
                children: /*#__PURE__*/_jsx(CartWithProjects, {
                  cart: groupedCartProjects[Number(chainId)],
                  chainId: Number(chainId),
                  balances: balances[chainId],
                  totalAmount: totalAmountByChainId[Number(chainId)],
                  enoughBalance: enoughBalanceByChainId[Number(chainId)],
                  payoutToken: getVotingTokenForChain(Number(chainId)),
                  handleSwap: () => swap(chainId)
                })
              }, Number(chainId)))
            }), /*#__PURE__*/_jsx("div", {
              className: "sm:col-span-1 order-2 mb-6 sm:mb-0",
              children: /*#__PURE__*/_jsx(SummaryContainer, {
                enoughBalanceByChainId: enoughBalanceByChainId,
                totalAmountByChainId: totalAmountByChainId,
                handleSwap: swap
              })
            })]
          })
        }), /*#__PURE__*/_jsx(GenericModal, {
          body: /*#__PURE__*/_jsx(SquidWidget, {
            ...swapParams
          }),
          isOpen: openSwapModel,
          setIsOpen: swapModalHandler,
          className: "z-50",
          isIframe: true
        })]
      }), /*#__PURE__*/_jsx("div", {
        className: "my-11",
        children: /*#__PURE__*/_jsx(Footer, {})
      })]
    })]
  });
}